import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';

import { OutletLogo } from '@/components/feed/FeedCard';
import { HighlightedSelections } from '@/components/HighlightedSelections';
import { isAbortError } from '@/hooks/liveSearchPaging';
import { apiClient, type FeedItem } from '@/services';
import { timeAgo } from '@/utils/timeAgo';

const pageSize = 10;

const footerButtonClassName =
  'flex h-12 items-center justify-center gap-1.5 text-[13px] font-semibold outline-none transition-colors hover:bg-[#f1efe8] focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4c6b5c] aria-disabled:cursor-default aria-disabled:opacity-35 aria-disabled:hover:bg-transparent';

export interface TrainingNewsQuotes {
  item: FeedItem | undefined;
  index: number;
  canGoPrevious: boolean;
  canGoNext: boolean;
  loadingMore: boolean;
  showPrevious: () => void;
  showNext: () => Promise<void>;
}

export function useTrainingNewsQuotes(cardId: string | undefined): TrainingNewsQuotes {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [index, setIndex] = useState(0);
  const [nextCursor, setNextCursor] = useState<string>();
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadedFor, setLoadedFor] = useState(cardId);
  const controllerRef = useRef<AbortController | null>(null);

  if (loadedFor !== cardId) {
    setLoadedFor(cardId);
    setItems([]);
    setIndex(0);
    setNextCursor(undefined);
    setHasMore(false);
    setLoadingMore(false);
  }

  useEffect(() => {
    if (!cardId) return;

    const controller = new AbortController();
    controllerRef.current = controller;

    apiClient
      .getFeedPage(null, pageSize, cardId, controller.signal)
      .then((page) => {
        if (controller.signal.aborted) return;
        setItems(page.items ?? []);
        setNextCursor(page.nextCursor);
        setHasMore(page.hasMore);
      })
      .catch((caught) => {
        if (!isAbortError(caught) && !controller.signal.aborted) setItems([]);
      });

    return () => controller.abort();
  }, [cardId]);

  const canLoadMore = hasMore && Boolean(nextCursor);

  const showPrevious = () => {
    if (loadingMore) return;
    setIndex((current) => Math.max(0, current - 1));
  };

  const showNext = async () => {
    if (loadingMore) return;
    if (index < items.length - 1) {
      setIndex(index + 1);
      return;
    }
    const controller = controllerRef.current;
    if (!canLoadMore || !controller || !cardId) {
      setIndex(0);
      return;
    }

    setLoadingMore(true);
    try {
      const page = await apiClient.getFeedPage(nextCursor, pageSize, cardId, controller.signal);
      if (controller.signal.aborted) return;
      const known = new Set(items.map((existing) => existing.matchId));
      const incoming = (page.items ?? []).filter((candidate) => !known.has(candidate.matchId));
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
      if (incoming.length === 0) {
        setIndex(0);
        return;
      }
      setItems([...items, ...incoming]);
      setIndex(items.length);
    } catch (caught) {
      if (!isAbortError(caught)) setIndex(0);
    } finally {
      if (!controller.signal.aborted) setLoadingMore(false);
    }
  };

  return {
    item: items[index],
    index,
    canGoPrevious: index > 0,
    canGoNext: items.length > 1 || canLoadMore,
    loadingMore,
    showPrevious,
    showNext,
  };
}

export function TrainingNewsQuote({ quotes, className = '' }: { quotes: TrainingNewsQuotes; className?: string }) {
  const { item, canGoPrevious, canGoNext, loadingMore } = quotes;
  if (!item) return null;

  const handlePrevious = (event: MouseEvent) => {
    event.stopPropagation();
    if (canGoPrevious) quotes.showPrevious();
  };

  const handleNext = (event: MouseEvent) => {
    event.stopPropagation();
    if (canGoNext) void quotes.showNext();
  };

  const highlights = (item.matchedTokens ?? [])
    .map((span) => ({ start: span.start, length: span.end - span.start }))
    .sort((left, right) => left.start - right.start);
  const outletName = item.siteName ?? item.articleDomain;
  const articleTitle = item.articleTitle ?? item.articleDomain;

  return (
    <section
      aria-label="In the news"
      aria-busy={loadingMore}
      onTouchStart={(event) => event.stopPropagation()}
      className={`flex flex-col gap-3 ${className}`}
    >
      <span className="text-[11px] font-bold uppercase tracking-[0.12em]" style={{ color: '#b45309' }}>
        In the news
      </span>

      <div className="flex min-w-0 items-center gap-2">
        <OutletLogo item={item} tiny />
        <a
          href={item.articleUrl}
          target="_blank"
          rel="noopener noreferrer"
          title={articleTitle}
          onClick={(event) => event.stopPropagation()}
          className="group/source flex min-w-0 items-center gap-1 rounded-sm text-[13px] font-semibold outline-none hover:underline focus-visible:ring-2 focus-visible:ring-[#4c6b5c] focus-visible:ring-offset-1"
          style={{ color: '#1c2230' }}
        >
          <span className="truncate">{outletName}</span>
          <span className="sr-only"> (opens article in a new tab)</span>
          <ExternalLink
            className="size-3 shrink-0 text-[#8a8a80] group-hover/source:text-[#4c6b5c]"
            aria-hidden="true"
          />
        </a>
        {item.publishedAtUtc && (
          <span
            className="shrink-0 whitespace-nowrap text-xs"
            style={{ color: '#8a8a80' }}
            title={item.publishedAtUtc.toLocaleDateString(undefined, { dateStyle: 'long' })}
          >
            · {timeAgo(item.publishedAtUtc)}
          </span>
        )}
      </div>

      <blockquote
        aria-live="polite"
        className="text-base italic leading-[1.55]"
        style={{ fontFamily: 'var(--font-display)', color: '#2a2f3a' }}
      >
        “
        <HighlightedSelections
          text={item.sentence}
          selections={highlights}
          renderHighlight={(text) => (
            <span
              className="rounded-[3px] px-0.5 font-semibold"
              style={{ background: '#fdeec4', color: '#b45309' }}
            >
              {text}
            </span>
          )}
        />
        ”
      </blockquote>

      <div className="mt-1 grid grid-cols-2 border-t border-[#e3e1d9]">
        <button
          type="button"
          aria-label="Previous quote"
          onClick={handlePrevious}
          aria-disabled={!canGoPrevious || loadingMore}
          className={`${footerButtonClassName} border-r border-[#e3e1d9] text-[#6b6b62]`}
        >
          <ChevronLeft className="size-3.5" aria-hidden="true" />
          Previous
        </button>
        <button
          type="button"
          aria-label="Next quote"
          onClick={handleNext}
          aria-disabled={!canGoNext || loadingMore}
          className={`${footerButtonClassName} text-[#b45309]`}
        >
          Next
          <ChevronRight className="size-3.5" aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}
